import { useMemo, useState } from 'react'
import type { ControllerListItem } from '@/features/controllers/types'
import { plural } from './severityRules'

/** The site selection used by "Subscribe by severity" and "Individual alarms". */
export default function SitePicker({
  controllers,
  selected,
  onChange,
}: {
  controllers: ControllerListItem[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const [search, setSearch] = useState('')
  const sorted = useMemo(
    () =>
      [...controllers].sort((a, b) =>
        (a.controller_name || a.controller_id).localeCompare(b.controller_name || b.controller_id),
      ),
    [controllers],
  )
  const term = search.trim().toLowerCase()
  const visible = term
    ? sorted.filter((c) => `${c.controller_name ?? ''} ${c.controller_id}`.toLowerCase().includes(term))
    : sorted

  function toggle(id: string) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  return (
    <section aria-labelledby="site-picker-heading" className="rounded-2xl border border-nav-line bg-card p-4">
      <h2 id="site-picker-heading" className="text-base font-semibold text-ink">
        1. Choose sites
      </h2>
      <p className="mt-1 text-xs text-text-secondary">
        Used by <strong>Subscribe by severity</strong> and <strong>Individual alarms</strong>.
      </p>
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search sites"
        aria-label="Search sites"
        className="mt-3 w-full rounded-full border border-nav-line bg-card px-3 py-1.5 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      />
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-text-secondary" aria-live="polite">
          {plural(selected.size, 'site')} selected
        </span>
        <span className="flex gap-3">
          <button
            type="button"
            onClick={() => onChange(new Set([...selected, ...visible.map((c) => c.controller_id)]))}
            disabled={visible.length === 0}
            className="font-semibold text-accent hover:underline disabled:opacity-50"
          >
            {term ? 'Select matches' : 'Select all'}
          </button>
          <button
            type="button"
            onClick={() => onChange(new Set())}
            disabled={selected.size === 0}
            className="font-semibold text-accent hover:underline disabled:opacity-50"
          >
            Clear
          </button>
        </span>
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto rounded-2xl border border-nav-line p-2 lg:max-h-[50vh]">
        {visible.length === 0 ? (
          <p className="p-2 text-xs text-text-secondary">No sites match “{search}”.</p>
        ) : (
          visible.map((c) => (
            <label key={c.controller_id} className="flex items-center gap-2 rounded-lg px-1 py-1 text-sm text-text-primary hover:bg-nav-hover">
              <input
                type="checkbox"
                checked={selected.has(c.controller_id)}
                onChange={() => toggle(c.controller_id)}
              />
              <span className="truncate">{c.controller_name || c.controller_id}</span>
            </label>
          ))
        )}
      </div>
    </section>
  )
}
